/**
 * Time extraction utilities.
 *
 * The Zoho migration stored Eastern times as UTC (e.g. a 9:00 AM ET meeting
 * was saved as "2026-04-14T09:00:00+00:00"). Running `new Date()` on these
 * strings then formatting with `format()` shifts them by the local timezone
 * offset, producing wrong times.
 *
 * These helpers extract hours/minutes directly from the raw ISO string so
 * the displayed time matches what was originally entered in Zoho.
 */

/** Extract "HH:mm" from a raw ISO/timestamptz string without timezone conversion. */
export function extractRawTime(isoString: string | null | undefined): string | null {
  if (!isoString) return null;
  // Match the T separator followed by HH:mm
  const match = isoString.match(/T(\d{2}):(\d{2})/);
  if (!match) return null;
  return `${match[1]}:${match[2]}`;
}

/** Format a raw "HH:mm" (24-hour) string to 12-hour display (e.g. "9:00 AM"). */
export function formatTime12h(hhmm: string | null): string {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":");
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${h12}:${m} ${ampm}`;
}

/**
 * Extract time from a timestamptz ISO string and return a 12-hour formatted
 * string (e.g. "9:00 AM"). Returns empty string if no time found.
 */
export function extractAndFormatTime(isoString: string | null | undefined): string {
  return formatTime12h(extractRawTime(isoString));
}
